import React from 'react';
import {
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Box,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { WarningAmber } from '@mui/icons-material';
import { Link } from 'react-router-dom';

export interface DashboardStatsCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  to?: string;
  onClick?: () => void;
  loading?: boolean;
  ariaLabel?: string;
  error?: string | null;
}

export const DashboardStatsCard: React.FC<DashboardStatsCardProps> = ({
  title,
  value,
  icon,
  color,
  to,
  onClick,
  loading = false,
  ariaLabel,
  error,
}) => {
  const content = (
    <CardContent sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minHeight: 68 }}>
        <Box
          sx={{
            p: 0.9,
            borderRadius: 1.5,
            bgcolor: `${color}.main`,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontSize: '1.1rem',
          }}
        >
          {icon}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>
            {title}
          </Typography>

          {loading ? (
            <CircularProgress size={18} aria-label={`${title} is loading`} />
          ) : error ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title={error}>
                <WarningAmber color="warning" fontSize="small" aria-label={`${title} unavailable`} />
              </Tooltip>
              <Typography variant="body2" color="warning.main" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                Unavailable
              </Typography>
            </Box>
          ) : (
            <Typography variant="h6" color={`${color}.main`} sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {value}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContent>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: `${color}.light`,
        boxShadow: 'none',
      }}
    >
      {to ? (
        <CardActionArea
          component={Link}
          to={to}
          aria-label={ariaLabel || `Open ${title}`}
          sx={{ '&:focus-visible': { outline: theme => `2px solid ${theme.palette[color].main}`, outlineOffset: 2 } }}
        >
          {content}
        </CardActionArea>
      ) : onClick ? (
        <CardActionArea
          onClick={onClick}
          aria-label={ariaLabel || `Open ${title}`}
          sx={{ '&:focus-visible': { outline: theme => `2px solid ${theme.palette[color].main}`, outlineOffset: 2 } }}
        >
          {content}
        </CardActionArea>
      ) : (
        <Box role="group" aria-label={ariaLabel || `${title} statistics`}>
          {content}
        </Box>
      )}
    </Card>
  );
};
